import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { marginService } from '@swyftx/margin';
import {
  MarginOrderDirection,
  MarginOrderType,
  PriceError,
  MarginPrice,
  MarginInstrument,
} from '@swyftx/margin/build/types';

type Props = {
  price?: string;
  tradeSide: MarginOrderDirection;
  tradeType: MarginOrderType;
  selectedMarketPrice?: MarginPrice;
  selectedMarket?: MarginInstrument;
  isOpenPosition?: boolean;
};

const useMarginTradePriceError = ({
  price,
  tradeSide,
  tradeType,
  selectedMarketPrice,
  selectedMarket,
  isOpenPosition,
}: Props) => {
  const { t } = useTranslation('margin');

  const error = useMemo(() => {
    if (!price || !selectedMarket || !selectedMarketPrice || isOpenPosition) return undefined;

    const priceError = marginService.getPriceError(
      tradeSide,
      tradeType,
      price,
      Number(tradeSide === MarginOrderDirection.Long ? selectedMarketPrice?.ask : selectedMarketPrice?.bid),
      selectedMarket.precision,
    );

    if (priceError === undefined) return undefined;

    const { isLimitOrder } = marginService.distilMarginInformation(tradeSide, tradeType);

    const type = isLimitOrder ? t(`tradeType.${MarginOrderType.Limit}`) : t(`tradeType.${MarginOrderType.Stop}`);

    switch (priceError) {
      case PriceError.LimitPriceAboveBuy:
        return t('protectionOrder.errors.limitPriceAboveAsk');
      case PriceError.LimitPriceBelowSell:
        return t('protectionOrder.errors.limitPriceBelowBid');
      case PriceError.PriceIsZero:
        return t('protectionOrder.errors.priceIsZero', { type });
      case PriceError.StopPriceAboveSell:
        return t('protectionOrder.errors.stopPriceAboveAsk');
      case PriceError.StopPriceBelowBuy:
        return t('protectionOrder.errors.stopPriceBelowBid');
      case PriceError.TooMuchPrecision:
        return undefined;
      default:
        priceError satisfies never;
    }
  }, [isOpenPosition, price, selectedMarket, selectedMarketPrice, t, tradeSide, tradeType]);

  return {
    error,
  };
};

export { useMarginTradePriceError };
